import { computed, ref } from "vue"

import { errorMessage } from "@/lib/errors"
import { postJson } from "@/lib/fetch"
import { captureAnalysesPath, itemsPath } from "@/routes"

/**
 * States the capture flow moves through.
 *
 * - `idle`            — viewfinder active, waiting for first interaction
 * - `voicePreHint`    — recording a voice hint before any photos
 * - `capturing`       — one or more photos taken, tray visible, shutter still accessible
 * - `photoStrip`      — reviewing photos in full drawer before analyzing
 * - `processing`      — AI analysis in progress (spinner drawer)
 * - `suggestion`      — AI suggestion card shown for confirm/edit
 * - `error`           — AI analysis failed, user can retry or go back
 * - `containerPrompt` — after confirming a container-mode capture, prompt to set as active
 */
export type CaptureState =
  | "idle"
  | "voicePreHint"
  | "capturing"
  | "photoStrip"
  | "processing"
  | "suggestion"
  | "error"
  | "containerPrompt"

/** Whether the user is cataloging items or creating a container. */
export type CaptureMode = "items" | "container"

/** Shape of the AI suggestion returned by the analysis endpoint. */
interface Suggestion {
  name: string
  description: string
  condition: string | null
  quantity: number
  model_number: string | null
  serial_number: string | null
  item_type_id: string | null
  item_type_name: string | null
}

/**
 * State machine composable for the capture flow.
 *
 * Manages the full lifecycle: idle → capture photos → review → AI
 * analysis → accept/edit suggestion → reset. Designed for two-mode
 * interaction: **capture mode** (tray + shutter visible for rapid
 * multi-photo) and **review mode** (full drawer for hint/analyze).
 *
 * @example
 * ```ts
 * const flow = useCaptureFlow()
 * flow.capture(dataUrl)   // idle → capturing
 * flow.review()           // capturing → photoStrip
 * flow.analyze()          // photoStrip → processing → suggestion
 * flow.confirm()          // suggestion → idle (or containerPrompt)
 * ```
 */
/** Parse the analysis API response into a typed Suggestion. */
function parseSuggestion(data: Record<string, unknown>): Suggestion {
  return {
    name: (data.name as string) ?? "",
    description: (data.description as string) ?? "",
    condition: (data.condition as string) ?? null,
    quantity: (data.quantity as number) ?? 1,
    model_number: (data.model_number as string) ?? null,
    serial_number: (data.serial_number as string) ?? null,
    item_type_id: (data.item_type_id as string) ?? null,
    item_type_name: (data.item_type_name as string) ?? null,
  }
}

export function useCaptureFlow() {
  /** Current position in the capture state machine. Starts at `idle`. */
  const state = ref<CaptureState>("idle")

  /** Whether we're cataloging an item or creating a container. */
  const mode = ref<CaptureMode>("items")

  /** Data URLs of captured photos, in capture order. */
  const photos = ref<string[]>([])

  /** Optional text hint the user provides before analysis (e.g. "Makita drill, serial on bottom"). */
  const hint = ref("")

  /** ID of the active container that items are captured into. Set by the shell. */
  const containerId = ref<string | null>(null)

  /** Whether the QR/barcode scanner overlay is active. Independent of capture state. */
  const scanning = ref(false)

  /** Status message shown in the scan overlay pill (null = default "Scanning..." text). */
  const scanStatus = ref<string | null>(null)

  /** Scanned barcode for an unknown code — attached as asset_code to the next item created. */
  const pendingAssetCode = ref<string | null>(null)

  /** Whether the container picker drawer is open. Independent of capture state. */
  const pickerOpen = ref(false)

  /** Maximum number of photos per capture session. */
  const MAX_PHOTOS = 10

  /** AbortController for in-flight requests. Aborted on reset to prevent stale state mutations. */
  let requestAbort: AbortController | null = null

  /** Human-readable error message when analysis or save fails. */
  const error = ref<string | null>(null)

  /** True while the confirm POST is in flight. Used for button loading state. */
  const saving = ref(false)

  /** Data returned from the last successfully created item. Used by container prompt. */
  const lastCreated = ref<{ id: string; name: string; is_container: boolean } | null>(null)

  /** Brief success message shown after an item is saved. Auto-clears after 2s. */
  const successPill = ref<{ name: string; thumbUrl: string | null } | null>(null)
  let successTimer: ReturnType<typeof setTimeout> | null = null

  /** AI-generated suggestion after analysis. Null until processing completes. */
  const suggestion = ref<Suggestion | null>(null)

  const isIdle = computed(() => state.value === "idle")
  const isVoicePreHint = computed(() => state.value === "voicePreHint")
  const isCapturing = computed(() => state.value === "capturing")
  const isPhotoStrip = computed(() => state.value === "photoStrip")
  const isProcessing = computed(() => state.value === "processing")
  const isSuggestion = computed(() => state.value === "suggestion")
  const isError = computed(() => state.value === "error")
  const isContainerPrompt = computed(() => state.value === "containerPrompt")

  /** Whether the bottom UI (controls + action bar) should be visible. */
  const showBottomUI = computed(() => isIdle.value || isVoicePreHint.value || isCapturing.value)

  /**
   * Call the analysis endpoint and update the suggestion.
   *
   * Shared by analyze() and refine(). Handles the full lifecycle:
   * transition to processing → POST → parse response → transition
   * to suggestion (or error on failure). Abortable via reset().
   */
  async function callAnalysis(body: Record<string, unknown>) {
    state.value = "processing"
    error.value = null
    requestAbort = new AbortController()

    try {
      const response = await postJson(captureAnalysesPath(), body, requestAbort.signal)
      requestAbort = null

      if (!response.ok) {
        const json = await response.json().catch(() => null)
        throw new Error(json?.error ?? `Analysis failed (${response.status})`)
      }

      suggestion.value = parseSuggestion(await response.json())
      state.value = "suggestion"
    } catch (err) {
      requestAbort = null
      if (err instanceof DOMException && err.name === "AbortError") return
      error.value = errorMessage(err, "Analysis failed")
      state.value = "error"
    }
  }

  /** Show the success pill for 2 seconds then auto-clear. */
  function showSuccessPill(name: string, thumbUrl: string | null) {
    if (successTimer) clearTimeout(successTimer)
    successPill.value = { name, thumbUrl }
    successTimer = setTimeout(() => {
      successPill.value = null
      successTimer = null
    }, 2000)
  }

  /**
   * Add a photo and transition to capturing state.
   *
   * Can be called multiple times — each call appends to the photos array.
   * Only transitions state on the first capture (from idle or voicePreHint).
   * Subsequent captures stay in `capturing`.
   */
  function capture(dataUrl: string) {
    if (photos.value.length >= MAX_PHOTOS) return
    photos.value.push(dataUrl)
    if (state.value === "idle" || state.value === "voicePreHint") {
      state.value = "capturing"
    }
  }

  /**
   * Open the full photo strip drawer for review.
   *
   * Guarded: only transitions from `capturing` → `photoStrip`.
   */
  function review() {
    if (state.value === "capturing") {
      state.value = "photoStrip"
    }
  }

  /** Remove a photo by index. Resets to idle if no photos remain. */
  function removePhoto(index: number) {
    photos.value.splice(index, 1)
    if (photos.value.length === 0) {
      state.value = "idle"
    }
  }

  /**
   * Start AI analysis of the captured photos.
   *
   * Transitions to `processing` immediately, then to `suggestion`
   * when the AI responds. On failure, transitions to `error`.
   */
  async function analyze() {
    if (state.value !== "capturing" && state.value !== "photoStrip" && state.value !== "error")
      return
    await callAnalysis({
      images: photos.value,
      hint: hint.value || null,
      is_container: mode.value === "container",
    })
  }

  /**
   * Re-analyze with a user correction applied to the existing suggestion.
   *
   * Sends the current suggestion as `existing` context and the correction
   * as `hint`, so the AI updates its answer based on both.
   */
  async function refine(correction: string) {
    if (state.value !== "suggestion" || !suggestion.value) return
    await callAnalysis({
      images: photos.value,
      hint: correction,
      is_container: mode.value === "container",
      existing: {
        name: suggestion.value.name,
        description: suggestion.value.description,
        condition: suggestion.value.condition,
        quantity: suggestion.value.quantity,
        model_number: suggestion.value.model_number,
        serial_number: suggestion.value.serial_number,
      },
    })
  }

  /**
   * Retry analysis after an error. Returns to photoStrip so the user
   * can adjust hint/photos before re-analyzing.
   */
  function retryAnalysis() {
    if (state.value !== "error") return
    error.value = null
    state.value = "photoStrip"
  }

  /**
   * Accept the AI suggestion and persist the item via POST /items.
   *
   * In items mode: resets after successful save.
   * In container mode: transitions to `containerPrompt` to ask whether
   * to set the new container as the active capture context.
   */
  async function confirm() {
    if (!suggestion.value) return
    saving.value = true
    requestAbort = new AbortController()

    try {
      const s = suggestion.value

      const response = await postJson(
        itemsPath(),
        {
          name: s.name,
          description: s.description,
          condition: s.condition,
          quantity: s.quantity,
          model_number: s.model_number,
          serial_number: s.serial_number,
          item_type_id: s.item_type_id,
          is_container: mode.value === "container",
          parent_id: containerId.value,
          images: photos.value,
          asset_code: pendingAssetCode.value,
        },
        requestAbort.signal,
      )

      requestAbort = null

      if (!response.ok) {
        const body = await response.json().catch(() => null)
        throw new Error(body?.errors?.join(", ") ?? `Save failed (${response.status})`)
      }

      const data = await response.json()
      lastCreated.value = { id: data.id, name: data.name, is_container: data.is_container }

      // Show success pill before reset clears photos
      showSuccessPill(data.name, photos.value[0] ?? data.thumb_url ?? null)

      if (mode.value === "container") {
        state.value = "containerPrompt"
      } else {
        reset()
      }
    } catch (err) {
      requestAbort = null
      if (err instanceof DOMException && err.name === "AbortError") return
      error.value = errorMessage(err, "Save failed")
      state.value = "error"
    } finally {
      saving.value = false
    }
  }

  /** Dismiss the suggestion and reset the flow. */
  function dismiss() {
    reset()
  }

  /**
   * Set the newly created container as active and reset.
   *
   * Called from CaptureContainerPrompt's "Set as container" button.
   * Switches mode back to items (now cataloging into the new container).
   */
  function setContainerAndReset() {
    mode.value = "items"
    reset()
  }

  /**
   * Full reset: return to idle, clear all captured data.
   *
   * Clears photos, hint, and suggestion. Does not touch mode,
   * scanning, or pickerOpen — those persist across captures.
   */
  function reset() {
    if (requestAbort) {
      requestAbort.abort()
      requestAbort = null
    }
    // Note: successTimer is intentionally NOT cleared here — the pill
    // should persist through resets so the user sees it while framing
    // the next shot. It auto-clears after 2s via its own timeout.
    state.value = "idle"
    photos.value = []
    hint.value = ""
    suggestion.value = null
    error.value = null
    lastCreated.value = null
    pendingAssetCode.value = null
  }

  /** Alias for {@link reset}. Used by the photo strip's Clear button. */
  function clear() {
    reset()
  }

  /** Toggle between items and container capture mode. */
  function toggleMode() {
    mode.value = mode.value === "items" ? "container" : "items"
  }

  /** Start recording a voice pre-hint. Only valid from idle state. */
  function startVoicePreHint() {
    if (state.value !== "idle") return
    state.value = "voicePreHint"
  }

  /** Stop recording and return to idle. */
  function stopVoicePreHint() {
    state.value = "idle"
  }

  /** Toggle the QR/barcode scanner overlay on/off. */
  function toggleScanning() {
    scanning.value = !scanning.value
    if (!scanning.value) scanStatus.value = null
  }

  /** Stop scanning and clear the status message. */
  function stopScanning() {
    scanning.value = false
    scanStatus.value = null
  }

  /** Toggle the container picker drawer open/closed. */
  function togglePicker() {
    pickerOpen.value = !pickerOpen.value
  }

  return {
    state,
    mode,
    photos,
    hint,
    suggestion,
    error,
    saving,
    lastCreated,
    successPill,
    containerId,
    scanning,
    scanStatus,
    pendingAssetCode,
    pickerOpen,

    isIdle,
    isVoicePreHint,
    isCapturing,
    isPhotoStrip,
    isProcessing,
    isSuggestion,
    isError,
    isContainerPrompt,
    showBottomUI,

    capture,
    review,
    removePhoto,
    analyze,
    refine,
    retryAnalysis,
    confirm,
    dismiss,
    clear,
    reset,
    toggleMode,
    startVoicePreHint,
    stopVoicePreHint,
    setContainerAndReset,
    toggleScanning,
    stopScanning,
    togglePicker,
  }
}
